import type { AddressPartsLike } from '@/lib/address-match';

/**
 * NOT-586 — the operands of "is billing the same place as service?" on the job
 * form, derived once so the two surfaces that ask cannot drift:
 *
 *   - `JobCustomerContactsSection` asks it to decide whether to show the amber
 *     "Different from service address" warning;
 *   - `jobFormDto` asks it to decide whether to PERSIST a billing address at
 *     all — omitting the key IS how a job records "same as service".
 *
 * They previously derived their own operands and disagreed. The DTO compared
 * its built `JobAddress` pair, which `normalizeAddress` and `manualAddress`
 * stamp with a `country` of 'US'; the form compared the raw values, which carry
 * a country only when the source states one. On a Canadian job the form saw
 * 'CA' against 'CA' (match, no warning) while the DTO saw 'CA' against the
 * stamped 'US' (no match) and persisted a redundant billing address the form
 * had just told the user did not exist.
 *
 * These return the values AS THE USER SUPPLIED THEM — a saved contact-address
 * row, or the manual-entry fields. The 'US' stamp stays where it belongs, on
 * the address that gets written: it is a wire default with consumers of its own
 * (tax, geocoding), and it is not evidence about which country the user meant.
 */
export interface JobAddressSelection {
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  saStreet: string;
  saStreet2: string;
  saCity: string;
  saState: string;
  saZip: string;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  baStreet: string;
  baStreet2: string;
  baCity: string;
  baState: string;
  baZip: string;
  contactAddresses: readonly AddressPartsLike[];
}

/** The service address the form currently points at, or nothing selected. */
export function serviceAddressForCompare(
  selection: JobAddressSelection,
): AddressPartsLike | undefined {
  if (selection.addressMode === 'manual') {
    return {
      street: selection.saStreet,
      street2: selection.saStreet2,
      city: selection.saCity,
      state: selection.saState,
      zip: selection.saZip,
    };
  }
  if (selection.selectedAddressIndex === null) {
    const hasTypedAddress = Boolean(
      selection.saStreet ||
        selection.saStreet2 ||
        selection.saCity ||
        selection.saState ||
        selection.saZip,
    );
    if (!hasTypedAddress) return undefined;
    return {
      street: selection.saStreet,
      street2: selection.saStreet2,
      city: selection.saCity,
      state: selection.saState,
      zip: selection.saZip,
    };
  }
  return selection.contactAddresses[selection.selectedAddressIndex];
}

/**
 * The billing address the form currently points at, or nothing selected.
 *
 * A catalog bill-to is NOT handled here: it is a third party's address that
 * lives outside `contactAddresses`, and the job never persists it as its own
 * billing address. The form passes that one in directly.
 */
export function billingAddressForCompare(
  selection: JobAddressSelection,
): AddressPartsLike | undefined {
  if (selection.billingMode === 'manual') {
    return {
      street: selection.baStreet,
      street2: selection.baStreet2,
      city: selection.baCity,
      state: selection.baState,
      zip: selection.baZip,
    };
  }
  if (selection.selectedBillingIndex === null) return undefined;
  return selection.contactAddresses[selection.selectedBillingIndex];
}
